// @flow
import { alerts, feedback } from '~/core/constants';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { getAlerts, getUserId } from '../user/selectors';
import { LOAD_NOTIFICATIONS, SAVE_NOTIFICATIONS } from './constants';
import { notifications as notificationsApi, user as userApi } from '~/core/api';
import { receivedNotifications, savedNotifications, saveNotificationsError } from './actions';

import { flowRight, identity, noop } from 'lodash/fp';
import { getConfig } from '~/core/state/config/selectors';
import handleApi from '~/core/state/handle-api';
import handleSaga from '~/core/state/handle-saga';
import type { Effect } from 'redux-saga';
import { requestAlert } from '../alerts/actions';
import type { SaveNotificationsAction } from './types';
import { toCelsius } from '~/core/utils/temperature';

const putAlert = flowRight(put, requestAlert);

export function* loadNotificationsSaga(): Generator<noop | Effect, *, *> {
    const [notifications, error] = yield call(handleApi(notificationsApi.get));
    yield put(receivedNotifications(error || notifications));
}

function* watchGetNotificationsSaga(): Generator<Effect, *, *> {
    yield takeLatest(LOAD_NOTIFICATIONS, handleSaga(loadNotificationsSaga));
}

export function* saveNotificationsSaga({ payload }: SaveNotificationsAction): Generator<Effect | noop, *, *> {
    const { id, hotEnabled, hotTemperature, coldEnabled, coldTemperature, warningsEnabled } = payload;
    const { UNEXPECTED_ERROR } = feedback.generic;
    const { ERROR } = alerts.types;
    const userId = yield select(getUserId);
    const unit = yield select(getConfig, 'temperatureUnit');

    const tempConvert = unit === 'F' ? toCelsius : identity;

    const userAlerts = yield select(getAlerts);

    const [result, notificationsSaveError] = yield call(
        handleApi(notificationsApi.save),
        id,
        coldEnabled,
        tempConvert(coldTemperature),
        hotEnabled,
        tempConvert(hotTemperature)
    );

    const [, setAlertsError] = yield call(
        handleApi(userApi.setAlerts),
        userId,
        userAlerts.set('warningsEmail', warningsEnabled).toJS()
    );

    if (notificationsSaveError || setAlertsError) {
        yield putAlert(UNEXPECTED_ERROR, ERROR);
        yield put(saveNotificationsError());
    }

    const successPayload = {
        ...result,
        id
    };

    yield put(savedNotifications(notificationsSaveError || setAlertsError || successPayload));
}

function* watchSaveNotificationsSaga(): Generator<Effect, *, *> {
    yield takeLatest(SAVE_NOTIFICATIONS, handleSaga(saveNotificationsSaga));
}

export default [watchGetNotificationsSaga, watchSaveNotificationsSaga];



// WEBPACK FOOTER //
// ./src/core/state/notifications/sagas.js